
import React, { JSX, ReactNode, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'

const SNAP_EPS = 0.002
const MAX_FIT_FACTOR = 4

export type OverlayFitMode =
  | { kind: 'design', width: number, height?: number }
  | { kind: 'factor' }
  | { kind: 'off' }

interface OverlayFitBox {
  scale: number
  boxWidth: number | null
  boxHeight: number | null
}

const IDENTITY: OverlayFitBox = { scale: 1, boxWidth: null, boxHeight: null }

const parseFitFactor = (raw: string | null): number => {
  if (raw === null) return 1
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed < 1) return 1
  const factor = Math.min(parsed, MAX_FIT_FACTOR)
  return Math.abs(factor - 1) <= SNAP_EPS ? 1 : factor
}

export const useOverlayFit = (mode: OverlayFitMode): OverlayFitBox => {
  const [searchParams] = useSearchParams()
  const fitParam = searchParams.get('fit')
  const [viewport, setViewport] = useState(() => ({
    w: globalThis.innerWidth,
    h: globalThis.innerHeight
  }))
  useEffect(() => {
    const onResize = (): void => {
      setViewport((prev) =>
        prev.w === globalThis.innerWidth && prev.h === globalThis.innerHeight
          ? prev
          : { w: globalThis.innerWidth, h: globalThis.innerHeight })
    }
    globalThis.addEventListener('resize', onResize)
    return () => globalThis.removeEventListener('resize', onResize)
  }, [])

  if (mode.kind === 'off' || fitParam === 'off') return IDENTITY
  if (mode.kind === 'design') {
    if (mode.width <= 0 || viewport.w <= 0) return IDENTITY
    const scale = viewport.w / mode.width
    if (Math.abs(scale - 1) <= SNAP_EPS) return IDENTITY
    return { scale, boxWidth: mode.width, boxHeight: mode.height ?? null }
  }
  const factor = parseFitFactor(fitParam)
  if (factor === 1 || viewport.w <= 0 || viewport.h <= 0) return IDENTITY
  return { scale: factor, boxWidth: viewport.w / factor, boxHeight: viewport.h / factor }
}

export const OverlayAutoFit = ({
  mode,
  children
}: {
  mode: OverlayFitMode
  children: ReactNode | ((supersampled: boolean) => ReactNode)
}): JSX.Element => {
  const { scale, boxWidth, boxHeight } = useOverlayFit(mode)
  const supersampled = scale !== 1
  const content = typeof children === 'function' ? children(supersampled) : children
  return (
    <div
      data-testid='overlay-auto-fit'
      style={supersampled
        ? {
            width: boxWidth ?? undefined,
            height: boxHeight ?? undefined,
            overflow: 'hidden',
            transform: `scale(${scale})`,
            transformOrigin: 'top left'
          }
        : undefined}
    >
      {content}
    </div>
  )
}
